/**
 * 중증도분류 평가결과 API 응답 → TriageEvaluationResultDto 정규화
 * API_연동_가이드: 성공 시 응답은 { data: { ... } } 구조.
 * 실제 API 필드명(snake_case 등)은 백엔드 스펙에 맞게 매핑.
 */

import type { TriageEvaluationResultDto, TriageStatsDto, TriagePatientDto } from '@/types/api/scenarioResult';
import type { TriagePatientRow } from '@/pages/results/triageShared';
import { formatInjury, formatSymptom, formatIntervention } from '@/constants/triageMapping';

function pickNumber(v: unknown): number | undefined {
	if (typeof v === 'number' && !Number.isNaN(v)) return v;
	if (typeof v === 'string') {
		const n = parseFloat(v);
		return !Number.isNaN(n) ? n : undefined;
	}
	return undefined;
}

function pickString(v: unknown): string {
	if (v == null) return '';
	if (typeof v === 'string') return v;
	if (typeof v === 'number') return String(v);
	return '';
}

/**
 * 초 단위 숫자를 mm:ss 형식으로 변환
 * @param seconds - 초 단위 숫자
 * @returns mm:ss 형식 문자열
 */
function formatSecondsToMMSS(seconds: number | undefined): string {
	if (seconds == null || !Number.isFinite(seconds) || seconds < 0) {
		return '';
	}
	const totalSec = Math.floor(seconds);
	const m = Math.floor(totalSec / 60);
	const s = totalSec % 60;
	return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function mapStats(raw: unknown): TriageStatsDto | undefined {
	if (raw == null || typeof raw !== 'object') return undefined;
	const o = raw as Record<string, unknown>;
	
	if (process.env.NODE_ENV === 'development') {
		console.log('[triageResultMapper] mapStats 입력:', {
			has_triage_accuracy: 'triage_accuracy' in o,
			triage_accuracy_value: o.triage_accuracy,
			has_casualties_count: 'casualties_count' in o,
			casualties_count_value: o.casualties_count,
			has_play_time: 'play_time' in o,
			play_time_value: o.play_time,
			keys: Object.keys(o).slice(0, 20),
		});
	}
	
	// triage_accuracy가 0~1 범위면 100 곱해서 퍼센트로
	let correctRate = pickNumber(o.correctRate ?? o.correct_rate ?? o.triage_accuracy);
	if (correctRate != null && correctRate > 0 && correctRate <= 1) {
		correctRate = Math.round(correctRate * 100);
	}
	
	// duration 처리: 숫자(초)면 mm:ss 형식으로 변환, 문자열이면 그대로 사용
	let duration = '';
	const durationRaw = o.duration ?? o.Duration ?? o.elapsed_time ?? o.elapsedTime ?? o.play_time;
	if (typeof durationRaw === 'number') {
		duration = formatSecondsToMMSS(durationRaw);
	} else {
		duration = pickString(durationRaw);
	}
	
	const result = {
		score: pickNumber(o.score ?? o.Score),
		correctRate,
		correctCount: pickNumber(o.correctCount ?? o.correct_count),
		totalCount: pickNumber(
			o.totalCount ?? o.total_count ?? o.totalItems ?? o.total ?? o.casualties_count
		),
		duration,
		underTriageRate: pickNumber(o.underTriageRate ?? o.under_triage_rate ?? o.underTriage),
		overTriageRate: pickNumber(o.overTriageRate ?? o.over_triage_rate ?? o.overTriage),
		unclassifiedRate: pickNumber(o.unclassifiedRate ?? o.unclassified_rate ?? o.unclassified),
	};
	
	if (process.env.NODE_ENV === 'development') {
		console.log('[triageResultMapper] mapStats 결과:', result);
	}
	
	return result;
}

function mapPatient(raw: unknown): TriagePatientDto | null {
	if (raw == null || typeof raw !== 'object') return null;
	const o = raw as Record<string, unknown>;
	
	// 디버깅: 첫 번째 환자만 로그
	if (o.name === '김건명' || o.patient_name === '김건명') {
		console.log('[mapPatient] 원본 데이터 (김건명):', {
			name: o.name ?? o.patient_name,
			good_triage: o.good_triage,
			selected_triage: o.selected_triage,
			correct: o.correct,
			answer: o.answer,
			keys: Object.keys(o).slice(0, 30),
		});
	}
	
	const patientName = pickString(o.patientName ?? o.patient_name ?? o.name);
	const correctAnswer = pickString(o.correctAnswer ?? o.correct_answer ?? o.correct ?? o.good_triage);
	const userAnswer = pickString(o.userAnswer ?? o.user_answer ?? o.answer ?? o.response ?? o.selected_triage);
	
	// Radiation 전용 필드들 (ARS, 2단계 분류, 환자 상세 정보)
	// firstCorrect/firstUser = 외상 분류 (일반 트리아지와 동일)
	// secondCorrect/secondUser = 복합 분류 (방사선 ARS 기반)
	const firstCorrectAnswer = pickString(
		o.firstCorrectAnswer ?? 
		o.first_correct_answer ?? 
		o.firstCorrect ?? 
		o.good_triage
	);
	const firstUserAnswer = pickString(
		o.firstUserAnswer ?? 
		o.first_user_answer ?? 
		o.firstAnswer ?? 
		o.selected_triage
	);
	const secondCorrectAnswer = pickString(
		o.secondCorrectAnswer ?? 
		o.second_correct_answer ?? 
		o.secondCorrect ?? 
		o.good_r_triage ??  // 방사선 API 필드
		o.good_radiation_triage ?? 
		o.radiation_triage ?? 
		o.correct_radiation_triage
	);
	const secondUserAnswer = pickString(
		o.secondUserAnswer ?? 
		o.second_user_answer ?? 
		o.secondAnswer ?? 
		o.selected_r_triage ??  // 방사선 API 필드
		o.selected_radiation_triage ?? 
		o.user_radiation_triage
	);
	const ars = pickString(o.ars ?? o.ARS);
	const walkAbility = pickString(o.walkAbility ?? o.walk_ability ?? o.can);
	const responsiveness = pickString(o.responsiveness);
	const respiration = pickString(o.respiration ?? o.breath_kind);
	const chiefComplaint = pickString(o.chiefComplaint ?? o.chief_complaint ?? o.symptoms);
	const intervention = pickString(o.intervention ?? o.aid_history);
	
	// comments는 배열일 수 있음 (i18n 키 배열)
	let comments = '';
	if (Array.isArray(o.comments)) {
		comments = o.comments.filter((c): c is string => typeof c === 'string').join(' | ');
	} else {
		comments = pickString(o.comments ?? o.comment ?? o.diagnosis);
	}
	
	// majorInjuries 배열
	let majorInjuries: string[] | undefined;
	if (Array.isArray(o.majorInjuries)) {
		majorInjuries = o.majorInjuries.filter((s): s is string => typeof s === 'string');
	} else if (Array.isArray(o.major_injuries)) {
		majorInjuries = o.major_injuries.filter((s): s is string => typeof s === 'string');
	}
	
	// KTAS 전용 필드들 (Angular 필드명 기준)
	const pastHistory = pickString(o.medical_history ?? o.pastHistory ?? o.past_history);
	
	// visitMotivation은 객체일 수 있음 (motivation.desc)
	let visitMotivation = '';
	if (o.motivation && typeof o.motivation === 'object') {
		const mot = o.motivation as Record<string, unknown>;
		visitMotivation = pickString(mot.desc ?? mot.description);
	}
	if (!visitMotivation) {
		visitMotivation = pickString(o.visitMotivation ?? o.visit_motivation);
	}
	
	// 활력징후 (KTAS dia_hr, Radiation pulse 통합)
	const vitalSigns = pickString(o.vitalSigns ?? o.vital_signs ?? o.vitals);
	const bloodPressure = o.dia_sbp && o.dia_dbp ? `${o.dia_sbp}/${o.dia_dbp}` : pickString(o.bloodPressure ?? o.blood_pressure ?? o.bp);
	const pulse = pickString(o.dia_hr ?? o.pulse ?? o.hr);
	const temperature = pickString(o.dia_t ?? o.temperature ?? o.temp);
	const spo2 = pickString(o.dia_spo2 ?? o.spo2 ?? o.SpO2);
	const bloodGlucose = pickString(o.dia_bst_min ?? o.bloodGlucose ?? o.blood_glucose ?? o.bst);
	
	// 의식수준 (correct_answers.gcs)
	let consciousnessLevel = '';
	if (o.correct_answers && typeof o.correct_answers === 'object') {
		const correct = o.correct_answers as Record<string, unknown>;
		consciousnessLevel = pickString(correct.gcs);
	}
	if (!consciousnessLevel) {
		consciousnessLevel = pickString(o.consciousnessLevel ?? o.consciousness_level);
	}
	
	// 사용자 답변 (user_answers)
	let answerGcs = '';
	let answerMainSymptom = '';
	let answerPathway = '';
	if (o.user_answers && typeof o.user_answers === 'object') {
		const user = o.user_answers as Record<string, unknown>;
		answerGcs = pickString(user.gcs);
		// symptoms는 배열일 수 있음
		if (Array.isArray(user.symptoms)) {
			answerMainSymptom = user.symptoms.filter((s): s is string => typeof s === 'string').join(', ');
		} else {
			answerMainSymptom = pickString(user.symptoms);
		}
		answerPathway = pickString(user.pathway ?? user.ktas_level);
	}
	if (!answerGcs) answerGcs = pickString(o.answerGcs ?? o.answer_gcs);
	if (!answerMainSymptom) answerMainSymptom = pickString(o.answerMainSymptom ?? o.answer_main_symptom);
	if (!answerPathway) answerPathway = pickString(o.answerPathway ?? o.answer_pathway);
	
	// 정답 (correct_answers)
	let correctGcs = '';
	let correctMainSymptom = '';
	let correctKtasLevel = '';
	let correctPathway = '';
	if (o.correct_answers && typeof o.correct_answers === 'object') {
		const correct = o.correct_answers as Record<string, unknown>;
		correctGcs = pickString(correct.gcs);
		// symptoms는 배열일 수 있음
		if (Array.isArray(correct.symptoms)) {
			correctMainSymptom = correct.symptoms.filter((s): s is string => typeof s === 'string').join(', ');
		} else {
			correctMainSymptom = pickString(correct.symptoms);
		}
		correctKtasLevel = pickString(correct.ktas_level);
		correctPathway = pickString(correct.pathway);
	}
	if (!correctGcs) correctGcs = pickString(o.correctGcs ?? o.correct_gcs);
	if (!correctMainSymptom) correctMainSymptom = pickString(o.correctMainSymptom ?? o.correct_main_symptom);
	if (!correctKtasLevel) correctKtasLevel = pickString(o.correctKtasLevel ?? o.correct_ktas_level ?? o.correct_triage);
	if (!correctPathway) correctPathway = pickString(o.correctPathway ?? o.correct_pathway);
	
	// gender 처리: boolean 또는 문자열 "true"/"false" 모두 처리
	let gender: boolean = false;
	if (typeof o.gender === 'boolean') {
		gender = o.gender;
	} else if (typeof o.Gender === 'boolean') {
		gender = o.Gender;
	} else if (typeof o.gender === 'string') {
		const normalized = o.gender.toLowerCase().trim();
		gender = normalized === 'true' || normalized === '1';
	}
	
	return {
		patientName,
		gender,
		age: o.age != null ? String(o.age) : '',
		correctAnswer,
		userAnswer,
		firstCorrectAnswer,
		firstUserAnswer,
		secondCorrectAnswer,
		secondUserAnswer,
		ars,
		walkAbility,
		responsiveness,
		respiration,
		pulse,
		majorInjuries,
		chiefComplaint,
		intervention,
		comments,
		pastHistory,
		visitMotivation,
		vitalSigns,
		bloodPressure,
		temperature,
		spo2,
		bloodGlucose,
		consciousnessLevel,
		answerGcs,
		answerMainSymptom,
		answerPathway,
		correctGcs,
		correctMainSymptom,
		correctKtasLevel,
		correctPathway,
	};
}

/**
 * API 응답을 TriageEvaluationResultDto로 정규화.
 * 응답이 { data: { stats, patients } } 또는 { stats, patients } 또는 배열(patients로 해석) 지원.
 */
export function normalizeTriageResult(apiResponse: unknown): TriageEvaluationResultDto {
	if (apiResponse == null) return {};

	console.log('[normalizeTriageResult] 입력 apiResponse:', {
		type: typeof apiResponse,
		hasData: apiResponse && typeof apiResponse === 'object' && 'data' in apiResponse,
		hasPatients: apiResponse && typeof apiResponse === 'object' && 'patients' in apiResponse,
		keys: apiResponse && typeof apiResponse === 'object' ? Object.keys(apiResponse).slice(0, 30) : [],
	});

	const raw = typeof apiResponse === 'object' && 'data' in (apiResponse as Record<string, unknown>)
		? (apiResponse as { data: unknown }).data
		: apiResponse;

	console.log('[normalizeTriageResult] raw 처리 후:', {
		type: typeof raw,
		hasPatients: raw && typeof raw === 'object' && 'patients' in raw,
		keys: raw && typeof raw === 'object' ? Object.keys(raw).slice(0, 30) : [],
	});

	if (raw == null) return {};
	if (Array.isArray(raw)) {
		const patients = raw
			.map((item) => mapPatient(item))
			.filter((p): p is TriagePatientDto => p != null);
		return { patients };
	}
	if (typeof raw !== 'object') return {};

	const obj = raw as Record<string, unknown>;
	const dataInner = obj.data;
	const fromNestedData =
		dataInner != null && typeof dataInner === 'object' && !Array.isArray(dataInner)
			? (dataInner as Record<string, unknown>)
			: null;
	const list =
		obj.patient_feedbacks ??  // 트리아지 평가 결과 (good_triage, selected_triage 포함) - 우선순위 1
		obj.patientFeedbacks ??
		obj.patients ??  // 환자 상세 정보 (can, diagnosis 등) - 우선순위 2
		obj.Patients ??
		obj.list ??
		obj.results ??
		(Array.isArray(dataInner) ? dataInner : undefined) ??
		(fromNestedData != null ? fromNestedData.patients : undefined);
	const statsRaw =
		obj.stats ??
		obj.Stats ??
		obj.summary ??
		obj.statistics ??
		(fromNestedData != null ? fromNestedData.stats : undefined) ??
		obj;  // stats 필드가 없으면 obj 자체를 시도

	const stats = mapStats(statsRaw);
	let patients: TriagePatientDto[] = [];

	if (Array.isArray(list)) {
		patients = list.map((item) => mapPatient(item)).filter((p): p is TriagePatientDto => p != null);
	} else if (typeof list === 'object' && list !== null && !Array.isArray(list)) {
		const single = mapPatient(list);
		if (single) patients = [single];
	}

	// radiation/triage: patient_feedbacks와 patients 배열 매칭 (앵귤러 방식)
	// patients 배열에 추가 상세 정보(can, diagnosis, symptoms, aid_history, breath_kind)가 있으면 매칭
	const patientsDetailArray = obj.patients ?? (fromNestedData != null ? fromNestedData.patients : undefined);
	
	console.log('[triageResultMapper] 매칭 시작:', {
		hasPatientsDetail: Array.isArray(patientsDetailArray),
		patientsDetailLength: Array.isArray(patientsDetailArray) ? patientsDetailArray.length : 0,
		feedbackPatientsLength: patients.length,
		feedbackNames: patients.map(p => p.patientName),
		detailNames: Array.isArray(patientsDetailArray) ? patientsDetailArray.map((p: any) => p.patient_name ?? p.name) : [],
	});
	
	if (Array.isArray(patientsDetailArray) && patientsDetailArray.length > 0 && patients.length > 0) {
		patients.forEach((feedback) => {
			const detailPatient = patientsDetailArray.find((el: any) => {
				const elName = String(el.patient_name ?? el.name ?? '');
				return elName === feedback.patientName;
			});
			
			console.log(`[triageResultMapper] 환자 "${feedback.patientName}" 매칭:`, {
				found: !!detailPatient,
				hasCan: detailPatient && 'can' in detailPatient,
				hasDiagnosis: detailPatient && 'diagnosis' in detailPatient,
				hasSymptoms: detailPatient && 'symptoms' in detailPatient,
				hasAidHistory: detailPatient && 'aid_history' in detailPatient,
			});
			
			if (detailPatient && typeof detailPatient === 'object') {
				const detail = detailPatient as Record<string, unknown>;
				
				// can 객체: {walk: boolean, move: boolean, breath: boolean}
				if (detail.can && typeof detail.can === 'object' && !Array.isArray(detail.can)) {
					const can = detail.can as Record<string, unknown>;
					// walkAbility: 보행여부
					if (!feedback.walkAbility) {
						feedback.walkAbility = can.walk === true ? 'Yes' : 'No';
					}
					// responsiveness: 반응여부
					if (!feedback.responsiveness) {
						feedback.responsiveness = can.move === true ? 'Yes' : 'No';
					}
				}
				
				// diagnosis 객체: {respiration_rate: number, pulse: number, bp_sp, bp_dp, oxygen, temperature}
				if (detail.diagnosis && typeof detail.diagnosis === 'object' && !Array.isArray(detail.diagnosis)) {
					const diagnosis = detail.diagnosis as Record<string, unknown>;
					// respiration: 호흡수
					if (!feedback.respiration && diagnosis.respiration_rate != null) {
						feedback.respiration = String(diagnosis.respiration_rate);
					}
					// pulse: 맥박수
					if (!feedback.pulse && diagnosis.pulse != null) {
						feedback.pulse = String(diagnosis.pulse);
					}
				}
				
				// comments 기본값 설정
				if (!feedback.comments) {
					feedback.comments = '적합한 분류입니다.';
				}
				
				// symptoms 배열: 주호소
				if (!feedback.chiefComplaint && Array.isArray(detail.symptoms)) {
					// symptoms 배열이 비어있지 않으면 처리 (빈 배열이면 "주호소가 없습니다" 표시)
					if (detail.symptoms.length > 0) {
						const symptoms = detail.symptoms
							.map((s: any) => {
								if (s && typeof s === 'object' && 'cc' in s) {
									const ccNum = typeof s.cc === 'number' ? s.cc : parseInt(String(s.cc), 10);
									if (!isNaN(ccNum)) {
										return formatSymptom(ccNum, 'ko');
									}
								}
								return pickString(s);
							})
							.filter((s: string) => s !== '');
						feedback.chiefComplaint = symptoms.join(', ');
					} else {
						feedback.chiefComplaint = ''; // 빈 문자열로 설정하여 UI에서 "주호소가 없습니다" 표시
					}
				}
				
				// aid_history 배열: 중재 (기도개방 여부)
				if (!feedback.intervention && Array.isArray(detail.aid_history)) {
					feedback.intervention = formatIntervention(detail.aid_history, 'ko');
				}
				
				// breath_kind: 호흡 종류
				if (!feedback.respiration && detail.breath_kind != null) {
					feedback.respiration = pickString(detail.breath_kind);
				}
				
				// injury 배열: 주요손상
				if (Array.isArray(detail.injury) && detail.injury.length > 0) {
					if (!feedback.majorInjuries || feedback.majorInjuries.length === 0) {
						feedback.majorInjuries = detail.injury
							.map((inj: any) => {
								if (inj && typeof inj === 'object') {
									const degreeNum = typeof inj.degree === 'number' ? inj.degree : parseInt(String(inj.degree), 10);
									const injuryNum = typeof inj.injury === 'number' ? inj.injury : parseInt(String(inj.injury), 10);
									if (!isNaN(degreeNum) && !isNaN(injuryNum)) {
										return formatInjury(injuryNum, degreeNum, 'ko');
									}
								}
								return '';
							})
							.filter((s: string) => s !== '');
					}
				}
				
				// ARS와 방사선 트리아지 필드 (Radiation 시나리오)
				if (!feedback.ars && detail.ars) feedback.ars = pickString(detail.ars);
				
				// 방사선 복합 분류 (secondCorrect/secondUser)
				// 1. triage.radiation에서 정답 가져오기
				if (!feedback.secondCorrectAnswer) {
					if (detail.triage && typeof detail.triage === 'object' && !Array.isArray(detail.triage)) {
						const triage = detail.triage as Record<string, unknown>;
						if (triage.radiation) {
							feedback.secondCorrectAnswer = pickString(triage.radiation);
						}
					}
				}
				// 2. radiation_triage.value에서 사용자 답변 가져오기
				if (!feedback.secondUserAnswer) {
					if (detail.radiation_triage && typeof detail.radiation_triage === 'object' && !Array.isArray(detail.radiation_triage)) {
						const radiationTriage = detail.radiation_triage as Record<string, unknown>;
						if (radiationTriage.value) {
							feedback.secondUserAnswer = pickString(radiationTriage.value);
						}
					}
				}
			}
		});
		
		console.log('[triageResultMapper] 매칭 완료 후 첫 번째 환자:', patients[0] ? {
			name: patients[0].patientName,
			correctAnswer: patients[0].correctAnswer,
			userAnswer: patients[0].userAnswer,
			walkAbility: patients[0].walkAbility,
			respiration: patients[0].respiration,
			pulse: patients[0].pulse,
		} : 'No patients');
	}

	// correctCount 계산: good_triage === selected_triage인 환자 수
	if (stats && patients.length > 0 && stats.correctCount == null) {
		const correctCount = patients.filter((p) => {
			const c = p.correctAnswer?.trim() || '';
			const u = p.userAnswer?.trim() || '';
			return c !== '' && c === u;
		}).length;
		stats.correctCount = correctCount;
	}

	return { stats, patients };
}

/** DTO(플랫폼 api2) → 카드/표 `TriagePatientRow` (나이·문자열 정리) */
export function triageDtosToPatientRows(dtos: TriagePatientDto[]): TriagePatientRow[] {
	return dtos.map((d) => {
		const age = d.age;
		const ageStr = age === '' || age == null ? '' : typeof age === 'number' ? String(age) : String(age);
		return {
			patientName: d.patientName,
			gender: d.gender,
			age: ageStr,
			correctAnswer: d.correctAnswer,
			userAnswer: d.userAnswer,
			firstCorrectAnswer: d.firstCorrectAnswer,
			firstUserAnswer: d.firstUserAnswer,
			secondCorrectAnswer: d.secondCorrectAnswer,
			secondUserAnswer: d.secondUserAnswer,
			ars: d.ars,
			walkAbility: d.walkAbility,
			responsiveness: d.responsiveness,
			respiration: d.respiration,
			pulse: d.pulse,
			majorInjuries: d.majorInjuries,
			chiefComplaint: d.chiefComplaint,
			intervention: d.intervention,
			comments: d.comments,
			pastHistory: d.pastHistory,
			visitMotivation: d.visitMotivation,
			vitalSigns: d.vitalSigns,
			bloodPressure: d.bloodPressure,
			temperature: d.temperature,
			spo2: d.spo2,
			bloodGlucose: d.bloodGlucose,
			consciousnessLevel: d.consciousnessLevel,
			answerGcs: d.answerGcs,
			answerMainSymptom: d.answerMainSymptom,
			answerPathway: d.answerPathway,
			correctGcs: d.correctGcs,
			correctMainSymptom: d.correctMainSymptom,
			correctKtasLevel: d.correctKtasLevel,
			correctPathway: d.correctPathway,
			correctRate: d.correctRate,
			correctRateByColor: d.correctRateByColor,
		};
	});
}

/**
 * BFF API demo_patients → TriagePatientDto 변환 (전체참가자 데이터)
 * 
 * @description
 * - API 응답(TriageLabPatientDto): 스네이크 케이스
 * - 애플리케이션 DTO(TriagePatientDto): 카멜 케이스
 * - gender: boolean 그대로 유지 (UI에서 변환)
 */
export function mapDemoPatientToTriageDto(raw: unknown, lang: 'ko' | 'en' = 'ko'): TriagePatientDto | null {
	if (raw == null || typeof raw !== 'object') return null;
	const o = raw as Record<string, unknown>;
	
	// API 응답: patient_name (스네이크 케이스)
	const patientName = pickString(o.patient_name);
	
	// API 응답: age (number)
	const age = typeof o.age === 'number' ? o.age : pickString(o.age) || '';
	
	// API 응답: gender (boolean 또는 문자열 "true"/"false")
	let gender: boolean = false;
	if (typeof o.gender === 'boolean') {
		gender = o.gender;
	} else if (typeof o.gender === 'string') {
		const normalized = o.gender.toLowerCase().trim();
		gender = normalized === 'true' || normalized === '1';
	}
	
	// API 응답: triage.start (정답 색상)
	let correctAnswer = '';
	if (o.triage && typeof o.triage === 'object') {
		const triage = o.triage as Record<string, unknown>;
		correctAnswer = pickString(triage.start);
	}
	
	// API 응답: correct_triage_rate (스네이크 케이스)
	const correctRate = pickNumber(o.correct_triage_rate);
	
	// API 응답: selected_triage_rate (스네이크 케이스, START 방식 4가지 색상)
	// 키: G/Y/R/B (대문자 1글자)
	let correctRateByColor: TriagePatientDto['correctRateByColor'];
	if (o.selected_triage_rate && typeof o.selected_triage_rate === 'object') {
		const rates = o.selected_triage_rate as Record<string, unknown>;
		correctRateByColor = {
			red: pickNumber(rates.R) ?? 0,
			yellow: pickNumber(rates.Y) ?? 0,
			green: pickNumber(rates.G) ?? 0,
			black: pickNumber(rates.B) ?? 0,
		};
	}
	
	// API 응답: can.walk (보행여부)
	let walkAbility = '';
	if (o.can && typeof o.can === 'object') {
		const can = o.can as Record<string, unknown>;
		walkAbility = can.walk === true ? '가능' : can.walk === false ? '불가능' : '';
	}
	
	// API 응답: avpu 또는 response (반응여부)
	let responsiveness = pickString(o.avpu);
	if (!responsiveness && o.response && typeof o.response === 'object') {
		const response = o.response as Record<string, unknown>;
		// GCS 점수로 판단
		const eyeOpening = pickNumber(response.eye_opening) ?? 0;
		const verbal = pickNumber(response.verbal) ?? 0;
		const motor = pickNumber(response.motor) ?? 0;
		const gcsTotal = eyeOpening + verbal + motor;
		if (gcsTotal >= 13) responsiveness = 'A (Alert)';
		else if (gcsTotal >= 9) responsiveness = 'V (Voice)';
		else if (gcsTotal >= 4) responsiveness = 'P (Pain)';
		else responsiveness = 'U (Unresponsive)';
	}
	
	// API 응답: diagnosis.respiration_rate, pulse (호흡수, 맥박수)
	let respiration = '';
	let pulse = '';
	if (o.diagnosis && typeof o.diagnosis === 'object') {
		const diagnosis = o.diagnosis as Record<string, unknown>;
		const respirationRate = pickNumber(diagnosis.respiration_rate);
		if (respirationRate != null) {
			respiration = `${respirationRate}회/분`;
		}
		const pulseRate = pickNumber(diagnosis.pulse);
		if (pulseRate != null) {
			pulse = `${pulseRate}회/분`;
		}
	}
	
	// API 응답: injury (주요손상)
	const majorInjuries: string[] = [];
	if (Array.isArray(o.injury)) {
		o.injury.forEach((item) => {
			if (item && typeof item === 'object') {
				const injury = item as Record<string, unknown>;
				const injuryType = pickNumber(injury.injury);
				const degree = pickNumber(injury.degree);
				if (injuryType != null && degree != null) {
					const injuryText = formatInjury(injuryType, degree, lang);
					if (injuryText) {
						majorInjuries.push(injuryText);
					}
				}
			}
		});
	}
	
	// API 응답: symptoms (주호소)
	let chiefComplaint = '';
	if (Array.isArray(o.symptoms) && o.symptoms.length > 0) {
		const symptomTexts: string[] = [];
		o.symptoms.forEach((item) => {
			if (item && typeof item === 'object') {
				const symptom = item as Record<string, unknown>;
				const cc = pickNumber(symptom.cc);
				if (cc != null) {
					const symptomText = formatSymptom(cc, lang);
					if (symptomText) {
						symptomTexts.push(symptomText);
					}
				}
			}
		});
		chiefComplaint = symptomTexts.join(', ');
		
		if (process.env.NODE_ENV === 'development') {
			console.log('[mapDemoPatientToTriageDto] 주호소 처리:', {
				patientName,
				symptomsRaw: o.symptoms,
				symptomTexts,
				chiefComplaint,
			});
		}
	}
	
	// API 응답: aid_answer 또는 aid_history (중재)
	let intervention = '';
	if (Array.isArray(o.aid_answer) && o.aid_answer.length > 0) {
		// aid_answer는 문자열 배열 (예: ["aLAa", "aLAb"])
		const aidTexts = o.aid_answer
			.map((aidId) => {
				if (typeof aidId === 'string') {
					// aid_id를 텍스트로 변환
					if (aidId === 'aLAa' || aidId === 'aLAb') return '기도개방';
					// 다른 aid_id도 여기에 추가
					return aidId; // 기본값: aid_id 그대로
				}
				return null;
			})
			.filter((text): text is string => text != null && text !== '');
		
		intervention = aidTexts.join(', ');
	} else if (Array.isArray(o.aid_history) && o.aid_history.length > 0) {
		// aid_history는 객체 배열 (예: [{ aid_id: "aLAa", time: 61 }])
		const aidTexts = o.aid_history
			.map((item) => {
				if (item && typeof item === 'object') {
					const aid = item as Record<string, unknown>;
					const aidId = aid.aid_id;
					if (typeof aidId === 'string') {
						if (aidId === 'aLAa' || aidId === 'aLAb') return '기도개방';
						return aidId;
					}
				}
				return null;
			})
			.filter((text): text is string => text != null && text !== '');
		
		intervention = aidTexts.join(', ');
	}
	
	if (!intervention) {
		intervention = '미수행';
	}
	
	if (process.env.NODE_ENV === 'development') {
		console.log('[mapDemoPatientToTriageDto] 중재 처리:', {
			patientName,
			aid_answer: o.aid_answer,
			aid_history: o.aid_history,
			intervention,
		});
	}
	
	// 애플리케이션 DTO 반환 (카멜 케이스)
	return {
		patientName,
		gender,  // ← boolean 그대로! UI에서 변환할 것
		age,
		correctAnswer,
		userAnswer: '', // 전체참가자 데이터에는 개인 답변 없음
		correctRate,
		correctRateByColor,
		walkAbility,
		responsiveness,
		respiration,
		pulse,
		majorInjuries,
		chiefComplaint,
		intervention,
	};
}

